import type { CSSProperties } from "react";
import type { PartnerBranding } from "@/hooks/usePartnerBranding";

/**
 * Tema do Sidebar v2 (figma-spec §3, §7; decisões S13–S15; Cyber C5).
 * Cores do parceiro só entram via variáveis CSS e só depois de validadas como hex ou hsl().
 */

const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;
const HSL_COLOR =
  /^hsl\(\s*(\d{1,3}(?:\.\d+)?)(?:deg)?\s*[,\s]\s*(\d{1,3}(?:\.\d+)?)%\s*[,\s]\s*(\d{1,3}(?:\.\d+)?)%\s*\)$/i;

export function safeCssColor(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  return HEX_COLOR.test(trimmed) || HSL_COLOR.test(trimmed) ? trimmed : null;
}

function toRgb(color: string): [number, number, number] | null {
  if (HEX_COLOR.test(color)) {
    const hex = color.length === 4 ? color.replace(/[0-9a-f]/gi, (c) => c + c).slice(1) : color.slice(1);
    return [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255) as [number, number, number];
  }
  const m = HSL_COLOR.exec(color);
  if (!m) return null;
  const h = (Number(m[1]) % 360) / 360;
  const s = Math.min(Number(m[2]), 100) / 100;
  const l = Math.min(Number(m[3]), 100) / 100;
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const channel = (t0: number) => {
    const t = t0 < 0 ? t0 + 1 : t0 > 1 ? t0 - 1 : t0;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  return [channel(h + 1 / 3), channel(h), channel(h - 1 / 3)];
}

function luminance([r, g, b]: [number, number, number]): number {
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** Texto sobre a cor do parceiro: branco ou navy-dark (tr-navy-dark), o que tiver mais contraste. */
function prefersDarkText(color: string): boolean {
  const rgb = toRgb(color);
  if (!rgb) return false;
  const lum = luminance(rgb);
  const NAVY_DARK_LUMINANCE = 0.018; // #0A2643
  return (lum + 0.05) / (NAVY_DARK_LUMINANCE + 0.05) > 1.05 / (lum + 0.05);
}

export interface SidebarClasses {
  surface: string;
  text: string;
  strong: string;
  sub: string;
  hover: string;
  ring: string;
  groupLabel: string;
  divider: string;
  border: string;
  icon: string;
  orgCard: string;
  orgAvatar: string;
  userAvatar: string;
  dot: string;
  sealText: string;
  sealChip: string;
  active: string;
  activeText: string;
  activeBar: string;
  primary: string;
  kbd: string;
  skeleton: string;
}

export interface SidebarTheme {
  classes: SidebarClasses;
  style: CSSProperties | undefined;
}

const DARK_BASE = {
  surface: "bg-tr-navy",
  text: "text-white/[0.82]",
  strong: "text-white",
  sub: "text-white/60",
  hover: "hover:bg-white/[0.06]",
  groupLabel: "text-white/45",
  divider: "bg-white/10",
  border: "border-white/15",
  icon: "text-white",
  orgCard: "border-white/15 bg-white/[0.06]",
  userAvatar: "bg-white/[0.14] text-white",
  dot: "bg-white/35",
  sealText: "text-white/60",
  sealChip: "bg-white/[0.08] text-white",
  skeleton: "bg-white/10",
};

const LIGHT_BASE: typeof DARK_BASE = {
  surface: "border-r border-tr-border bg-white",
  text: "text-tr-ink",
  strong: "text-tr-ink",
  sub: "text-tr-muted",
  hover: "hover:bg-tr-canvas",
  groupLabel: "text-tr-muted",
  divider: "bg-tr-border",
  border: "border-tr-border",
  icon: "text-tr-muted",
  orgCard: "border-transparent bg-tr-canvas",
  userAvatar: "bg-tr-navy text-white",
  dot: "bg-tr-muted/50",
  sealText: "text-tr-muted",
  sealChip: "bg-tr-canvas text-tr-ink",
  skeleton: "bg-tr-canvas",
};

const MINT_ACCENT = {
  ring: "focus-visible:ring-tr-mint",
  orgAvatar: "bg-tr-mint text-tr-navy-dark",
  active: "bg-tr-mint/[0.14] font-semibold text-tr-mint",
  activeText: "font-semibold text-tr-mint",
  activeBar: "bg-tr-mint",
  primary: "bg-tr-mint text-tr-navy-dark hover:bg-tr-mint/90",
  kbd: "bg-tr-navy-dark/[0.22] text-tr-navy-dark",
};

/** Parceiro em fundo branco sem cor válida cadastrada: navy no lugar do mint (contraste sobre branco). */
const NAVY_ACCENT: typeof MINT_ACCENT = {
  ring: "focus-visible:ring-tr-navy",
  orgAvatar: "bg-tr-navy/[0.14] text-tr-navy",
  active: "bg-tr-navy/[0.07] font-semibold text-tr-navy",
  activeText: "font-semibold text-tr-navy",
  activeBar: "bg-tr-navy",
  primary: "bg-tr-navy text-white hover:bg-tr-navy/90",
  kbd: "bg-white/15 text-white",
};

/** Cor do parceiro via variáveis `--sb-accent` (item ativo) e `--sb-primary` (botão principal). */
const PARTNER_ACCENT: typeof MINT_ACCENT = {
  ring: "focus-visible:ring-[var(--sb-accent)]",
  orgAvatar: "bg-[color-mix(in_srgb,var(--sb-primary)_14%,transparent)] text-[color:var(--sb-primary)]",
  active: "bg-[color-mix(in_srgb,var(--sb-accent)_12%,transparent)] font-semibold text-[color:var(--sb-accent)]",
  activeText: "font-semibold text-[color:var(--sb-accent)]",
  activeBar: "bg-[var(--sb-accent)]",
  primary: "bg-[var(--sb-primary)] text-[color:var(--sb-on-primary)] hover:opacity-90",
  kbd: "bg-black/[0.15] text-[color:var(--sb-on-primary)]",
};

type CssVars = CSSProperties & Record<`--${string}`, string>;

export function buildSidebarTheme(branding: PartnerBranding | null, hasPartnerBranding: boolean): SidebarTheme {
  if (!hasPartnerBranding || !branding) return { classes: { ...DARK_BASE, ...MINT_ACCENT }, style: undefined };

  const isCustom = branding.sidebarMode === "custom";
  const customBg = isCustom ? safeCssColor(branding.sidebarBgColor) : null;
  const activeColor = safeCssColor(branding.sidebarActiveColor);
  const primary = safeCssColor(branding.corPrimaria) ?? activeColor;
  const accent = activeColor ?? primary;
  const base = isCustom ? DARK_BASE : LIGHT_BASE;

  if (!primary || !accent) {
    const fallback = isCustom ? MINT_ACCENT : NAVY_ACCENT;
    const surface = customBg ? "" : base.surface;
    return { classes: { ...base, ...fallback, surface }, style: customBg ? { backgroundColor: customBg } : undefined };
  }

  const style: CssVars = {
    "--sb-accent": accent,
    "--sb-primary": primary,
    "--sb-on-primary": prefersDarkText(primary) ? "#0A2643" : "#FFFFFF",
  };
  if (customBg) style.backgroundColor = customBg;
  return { classes: { ...base, ...PARTNER_ACCENT, surface: customBg ? "" : base.surface }, style };
}
